/**
 * Email Campaign List Component
 *
 * Displays campaigns in a filterable, searchable grid layout with preview images.
 * Combines search/filter functionality from TriggersTab with card-based display.
 *
 * @created 2025-10-30
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, RefreshCw } from 'lucide-react';
import { EmailCampaign } from '@/api/email';
import CampaignCard from './CampaignCard';

interface EmailCampaignListProps {
  campaigns: EmailCampaign[];
  loading: boolean;
  onEdit: (campaign: EmailCampaign) => void;
  onDelete?: (campaign: EmailCampaign) => void;
  onCapturePreview: (campaign: EmailCampaign) => void;
  onPreview?: (campaign: EmailCampaign) => void;
  onRefresh: () => void;
  userRole?: string;
}

/**
 * EmailCampaignList Component
 *
 * Features:
 * - Search by name, subject, description
 * - Filter by status (draft, scheduled, sending, sent, failed)
 * - Filter by audience type (all, filtered, custom)
 * - Grid layout with campaign cards
 * - Loading states and empty states
 *
 * @example
 * <EmailCampaignList
 *   campaigns={campaigns}
 *   loading={loading}
 *   onEdit={handleEdit}
 *   onCapturePreview={handleCapturePreview}
 *   onRefresh={fetchCampaigns}
 * />
 */
const EmailCampaignList: React.FC<EmailCampaignListProps> = ({
  campaigns,
  loading,
  onEdit,
  onDelete,
  onCapturePreview,
  onPreview,
  onRefresh,
  userRole
}) => {
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [audienceTypeFilter, setAudienceTypeFilter] = useState<string>('all_campaigns');

  // Filter campaigns based on search and filters
  const filteredCampaigns = campaigns.filter(campaign => {
    // Search match (name, subject, description)
    const matchesSearch = !searchTerm ||
      campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.campaign_subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());

    // Status match
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;

    // Audience type match
    // 'all_campaigns' = show all campaigns regardless of audience_type
    // 'all' = show campaigns with audience_type='all' (all subscribers/unfiltered)
    // 'filtered' = show campaigns with audience_type='filtered'
    const matchesAudienceType = audienceTypeFilter === 'all_campaigns' ||
      campaign.audience_type === audienceTypeFilter;

    return matchesSearch && matchesStatus && matchesAudienceType;
  });

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Filters skeleton */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-40">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-48">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="w-10">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>

        {/* Cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i}>
              <CardContent className="p-0">
                <div className="aspect-[4/3] bg-gray-200 rounded-t-lg animate-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-3/4 animate-pulse" />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="h-3 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <div className="flex items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>

        {/* Audience Type Filter */}
        <Select value={audienceTypeFilter} onValueChange={setAudienceTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Audience" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_campaigns">All Campaigns</SelectItem>
            <SelectItem value="all">All Subscribers</SelectItem>
            <SelectItem value="filtered">Filtered</SelectItem>
            {/* Custom removed - feature not yet implemented (Coming Soon in AudienceSelector) */}
          </SelectContent>
        </Select>

        {/* Refresh Button */}
        <Button
          variant="outline"
          size="icon"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Results Count */}
      {!loading && campaigns.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Showing {filteredCampaigns.length} of {campaigns.length} campaign{campaigns.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Campaign Grid */}
      {filteredCampaigns.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampaigns.map(campaign => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onEdit={onEdit}
              onDelete={onDelete}
              onCapturePreview={onCapturePreview}
              onPreview={onPreview}
              userRole={userRole}
            />
          ))}
        </div>
      ) : (
        /* Empty State */
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              {searchTerm || statusFilter !== 'all' || audienceTypeFilter !== 'all_campaigns' ? (
                <>
                  <p className="text-lg font-medium mb-2">No campaigns match your filters</p>
                  <p className="text-sm">Try adjusting your search or filter criteria</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium mb-2">No campaigns yet</p>
                  <p className="text-sm">Create your first email campaign to get started</p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailCampaignList;
